<template>
    <!-- Multi-alarm list modal: shown when a Section has >1 active alarm so the user can pick one.
         For single alarms the parent skips this modal and opens the ActionModal directly.
         Teleported to <body>; driven entirely by parent state (`open` prop). -->
    <Teleport to="body">
        <FadeTransition>
            <div
                v-if="open"
                class="fixed inset-0 z-[1030] flex items-center justify-center bg-black/40 p-4"
                @click.self="emit('close')"
            >
                <div
                    class="relative w-full max-w-sm rounded-xl bg-white shadow-xl"
                    role="dialog"
                    aria-modal="true"
                >
                    <!-- Header -->
                    <div class="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
                        <div class="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-red-100">
                            <AlertTriangle class="h-4 w-4 text-red-600" aria-hidden="true" />
                        </div>
                        <h2 class="flex-1 text-sm font-semibold text-gray-900">
                            {{ $t('views.monitoring.activeGroups.alarmHandle.title') }}
                        </h2>
                        <button
                            type="button"
                            class="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                            @click="emit('close')"
                        >
                            <X class="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>

                    <!-- Alarm list -->
                    <ul class="divide-y divide-gray-100 px-2 py-2">
                        <li v-for="alarm in alarms" :key="alarm.id">
                            <button
                                type="button"
                                class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:opacity-50"
                                :disabled="isLoading"
                                @click="emit('selectAlarm', alarm.id)"
                            >
                                <!-- Severity dot -->
                                <span
                                    class="h-2 w-2 shrink-0 rounded-full"
                                    :class="severityColour(alarm.severity_level)"
                                />
                                <span class="flex-1 text-sm font-medium text-gray-700">
                                    {{ alarmTypeName(alarm.type_id) }}
                                </span>
                                <Loader2 v-if="isLoading" class="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400" aria-hidden="true" />
                                <ChevronRight v-else class="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </FadeTransition>
    </Teleport>
</template>

<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { computed } from 'vue';
import { AlertTriangle, ChevronRight, Loader2, X } from 'lucide-vue-next';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import type { SectionAlarm } from '@/types/SectionLiveKpi';

const props = defineProps<{
    open: boolean;
    alarms: SectionAlarm[];
    isLoading?: boolean;
}>();

const emit = defineEmits<{
    close: [];
    selectAlarm: [id: number];
}>();

// Alarm type lookup from the store. GetAlarmTypes is lightweight and already loaded by the alarm log;
// if not yet loaded, dispatch it now so subsequent opens are instant.
const alarmTypes = computed(() => store.state.alarmTypes);

function alarmTypeName(typeId: number): string {
    const match = alarmTypes.value?.find((t) => t.id === typeId);
    return match?.name ?? `Alarm #${typeId}`;
}

// Severity level: 3 = error (red), 2 = warning (amber), 1 = ok (green).
function severityColour(level: number): string {
    if (level >= 3) return 'bg-red-500';
    if (level >= 2) return 'bg-amber-400';
    return 'bg-green-500';
}

// Ensure alarm types are in the store so the list can show meaningful labels.
if (!alarmTypes.value) {
    store.dispatch(ActionType.GetAlarmTypes, {});
}

onKeyStroke('Escape', () => {
    if (props.open) emit('close');
});
</script>
